<template>
  <div v-if="canMakePayments" :class="buttonClasses" @click="onClick" class="apple-pay-button">
    <slot :type="type">
      <span class="text">{{ $t('Pay with') }}</span>
      <span class="logo" ><img height="50" src="/assets/payment/applePay.png"></span>
    </slot>
  </div>
</template>

<script>
import config from 'config'
import props from 'src/modules/ipay-payment/helpers/applePayDefaulProps'
const lineItemsToDisplay = ['subtotal', 'shipping', 'fee']
import { mapActions } from 'vuex'

export default {
  name: 'ApplePayButton',
  props: {
    totals: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: false,
      default: 'check-out',
      validator: function (value) {
        return (
          [
            'plain',
            'buy',
            'set-up',
            'donate',
            'check-out',
            'book',
            'subscribe',
          ].indexOf(value) !== -1
        );
      },
    },
    color: {
      type: String,
      required: false,
      default: 'black',
      validator: function (value) {
        return ['black', 'white'].indexOf(value) !== -1;
      },
    },
    withLine: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    options: {
      type: Object,
      default: function () {
        return props
      }
    }
  },
  data () {
    return {
      canMakePayments: false,
      session: null
    }
  },
  mounted () {
    let merchantId = config.ipay.applepay.merchantInfo.merchantId
    if (merchantId && window.ApplePaySession) {
      this.setupButton(merchantId)
    }
  },
  methods: {
    ...mapActions({
      validateSession: 'ipay-payment-apple/validateApplePaySession'
    }),
    async setupButton (merchantId) {
      try {
        const _canMakePayments = await window.ApplePaySession.canMakePayments(merchantId)
        if (_canMakePayments) {
          this.canMakePayments = true
        }
      } catch (err) {
        this.$emit('error', err)
      }
    },
    async onValidateMerchant (event) {
      // 1. You call your server, passing it the URL from the event’s validationURL property.
      // 2. Your server uses the validation URL to request a session from the Apple Pay server,
      //    as described in Requesting an Apple Pay Payment Session.
      //    Never send the request for a merchant session from the client.
      // 3. In response, your server receives an opaque merchant session object.
      // 4. You pass the merchant session object to your Apple Pay session’s
      //    completeMerchantValidation method. You can use the merchant session object a single time.
      //    It expires five minutes after it is created.
      try {
        let validatedSession = await this.validateSession({ validationURL: event.validationURL })
        this.session.completeMerchantValidation(validatedSession)
      } catch (err) {
        this.$emit('error', err)
      }
    },
    async onPaymentAuthorized (event) {
      // The onpaymentauthorized function must complete the payment
      // and respond by calling completePayment before the 30 second timeout,
      // after which a message appears stating that the payment could not be completed.
      console.log(event)
      const paymentToken = event && event.payment && event.payment.token ? event.payment.token : false
      this.$emit('payed', { token: paymentToken })
      if (paymentToken) {
        this.session.completePayment({ status: window.ApplePaySession.STATUS_SUCCESS })
      } else {
        this.session.completePayment({ status: window.ApplePaySession.STATUS_FAILURE })
        this.$emit('error', event);
      }
    },
    async onCancel (event) {
      this.session.abort()
      this.$emit('cancel')
    },
    async onClick (e) {
      if (this.disabled) {
        return
      }
      this.$emit('click');
      const request = {
        countryCode: 'UA',
        currencyCode: 'UAH',
        supportedNetworks: this.options.supportedNetworks,
        merchantCapabilities: this.options.merchantCapabilities,
        total: {
          label: 'Total',
          amount: this.total.toFixed(2),
        },
        lineItems: this.lineItems,
      }
      this.session = new ApplePaySession(3, request)
      this.session.onvalidatemerchant = this.onValidateMerchant
      this.session.onpaymentauthorized = this.onPaymentAuthorized
      this.session.oncancel = this.onCancel
      this.session.begin()
    }
  },
  computed: {
    buttonClasses () {
      if (this.type === 'buy') {
        return `apple-pay-button-with-text apple-pay-button-${this.color}${
          this.withLine ? '-with-line' : ''
        }-with-text`;
      } else {
        return `apple-pay-button apple-pay-${
          this.type
        }-button apple-pay-button-${this.color}${
          this.withLine ? '-with-line' : ''
        }`;
      }
    },
    lineItems () {
      const lineItems = []
      for (const key in this.totals) {
        if (Object.hasOwnProperty.call(this.totals, key)) {
          const amount = this.totals[key]
          if (lineItemsToDisplay.includes(key)) {
            lineItems.push({ label: key, type: 'final', amount: amount.toFixed(2) })
          }
        }
      }
      return lineItems
    },
    total () {
      return this.totals.total || 0
    }
  }
}
</script>
<style lang="scss">
.apple-pay-button {
  width: 100%;
  height: 45px;
  background: #fff;
  border: 1px solid #000000;
  display: -ms-flexbox;
  display: flex;
  place-content: center;
  -ms-flex-align: center;
  align-items: center;
  font-size: 21px;
  font-weight: 700;
  border-radius: 5px;
  cursor: pointer;
  .logo {
    margin-left: 10px;
    img {
      margin-top: 8px;
    }
  }
}
</style>
